import React, { useState } from 'react';

async function searchOpenLibrary(query) {
    if (!query.trim()) return [];
    try {
        const url = `https://openlibrary.org/search.json?q=${encodeURIComponent(query)}&limit=8&fields=title,author_name,cover_i`;
        const res = await fetch(url);
        if (!res.ok) return [];
        const data = await res.json();
        return (data.docs || []).map(d => ({
            title: d.title,
            author: (d.author_name || [])[0] || '',
            coverId: d.cover_i || null,
        }));
    } catch {
        return [];
    }
}

export default function ConvertToSeriesModal({ book, myOtherBooks, onClose, onSave }) {
    const [seriesName, setSeriesName] = useState('');
    const [author, setAuthor] = useState(book.author || '');
    const [newBooks, setNewBooks] = useState([book.title]);
    const [absorbIds, setAbsorbIds] = useState(new Set());
    const [draft, setDraft] = useState('');
    const [addMode, setAddMode] = useState('manual');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);

    const sorted = [...myOtherBooks].sort((a, b) => {
        const lc = (book.author || '').toLowerCase();
        const aMatch = (a.author || '').toLowerCase() === lc;
        const bMatch = (b.author || '').toLowerCase() === lc;
        return aMatch === bMatch ? 0 : aMatch ? -1 : 1;
    });

    async function runSearch() {
        if (!query.trim()) return;
        setSearching(true);
        setResults([]);
        setResults(await searchOpenLibrary(query));
        setSearching(false);
    }

    function addManual() {
        const t = draft.trim();
        if (!t) return;
        setNewBooks(b => [...b, t]);
        setDraft('');
    }

    function addFromSearch(result) {
        setNewBooks(b => [...b, result.title]);
        setResults([]);
        setQuery('');
    }

    function toggleAbsorb(id) {
        setAbsorbIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }

    function submit(e) {
        e.preventDefault();
        onSave({
            seriesName: seriesName.trim(),
            author: author.trim(),
            absorbIds: Array.from(absorbIds),
            newBooks,
        });
    }

    const totalBooks = newBooks.length + absorbIds.size;
    const canSave = seriesName.trim() && totalBooks > 0;

    return (
        <div className="modal-scrim" onClick={onClose}>
            <form className="modal" onClick={e => e.stopPropagation()} onSubmit={submit}>
                <header className="modal-hd">
                    <h2>Turn into series</h2>
                    <button type="button" className="modal-close" onClick={onClose}>close ×</button>
                </header>

                <div className="modal-body">
                    <div className="gate-field">
                        <label className="gate-label">Series name</label>
                        <input
                            className="gate-input"
                            type="text"
                            value={seriesName}
                            onChange={e => setSeriesName(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className="gate-field">
                        <label className="gate-label">Author</label>
                        <input
                            className="gate-input"
                            type="text"
                            value={author}
                            onChange={e => setAuthor(e.target.value)}
                        />
                    </div>

                    <div className="gate-field">
                        <label className="gate-label">Books in this series</label>
                        <ul className="series-list">
                            {newBooks.map((t, i) => (
                                <li className="series-list-item" key={i}>
                                    <span className="series-list-num">{String(i + 1).padStart(2, '0')}</span>
                                    <span>{t}</span>
                                    <button type="button" className="series-list-x" onClick={() => setNewBooks(b => b.filter((_, j) => j !== i))}>×</button>
                                </li>
                            ))}
                            {newBooks.length === 0 && (
                                <li style={{ fontStyle: 'italic', color: 'var(--ink-3)', padding: '6px 4px' }}>No books yet.</li>
                            )}
                        </ul>

                        <div className="toggle-row" style={{ marginTop: 10, marginBottom: 8 }}>
                            <button type="button" className={'toggle-btn' + (addMode === 'manual' ? ' is-on' : '')} onClick={() => setAddMode('manual')}>Manual</button>
                            <button type="button" className={'toggle-btn' + (addMode === 'search' ? ' is-on' : '')} onClick={() => { setAddMode('search'); setResults([]); }}>Search</button>
                        </div>

                        {addMode === 'manual' && (
                            <div className="series-input-row">
                                <input
                                    className="gate-input"
                                    type="text"
                                    placeholder="Book title — Enter to add"
                                    value={draft}
                                    onChange={e => setDraft(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addManual(); } }}
                                />
                                <button type="button" className="add-btn" onClick={addManual}>
                                    <span className="add-btn-plus">+</span><span>add</span>
                                </button>
                            </div>
                        )}

                        {addMode === 'search' && (
                            <div>
                                <div className="search-row">
                                    <input
                                        className="gate-input"
                                        type="text"
                                        placeholder="Search Open Library…"
                                        value={query}
                                        onChange={e => setQuery(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
                                    />
                                    <button type="button" className="search-btn" onClick={runSearch} disabled={!query.trim()}>
                                        Search →
                                    </button>
                                </div>
                                {searching && <div className="search-status">Searching…</div>}
                                {results.length > 0 && (
                                    <ul className="search-results-list">
                                        {results.map((r, i) => (
                                            <li key={i} className="search-result-item" onClick={() => addFromSearch(r)}>
                                                {r.coverId && (
                                                    <img className="search-result-cover" src={`https://covers.openlibrary.org/b/id/${r.coverId}-S.jpg`} alt="" />
                                                )}
                                                <div className="search-result-info">
                                                    <div className="search-result-title">{r.title}</div>
                                                    {r.author && <div className="search-result-author">{r.author}</div>}
                                                </div>
                                                <span className="search-result-pick">+ add</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>

                    {sorted.length > 0 && (
                        <div className="gate-field">
                            <label className="gate-label">Pull in from your list</label>
                            <ul className="absorb-list">
                                {sorted.map(b => (
                                    <li
                                        key={b.id}
                                        className={'absorb-item' + (absorbIds.has(b.id) ? ' is-selected' : '')}
                                        onClick={() => toggleAbsorb(b.id)}
                                    >
                                        <span className="absorb-check">{absorbIds.has(b.id) ? '✓' : ''}</span>
                                        <span className="absorb-title">{b.title}</span>
                                        {b.author && <span className="absorb-author">{b.author}</span>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <footer className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button type="submit" className="btn-primary" disabled={!canSave}>
                        Create series ({totalBooks} book{totalBooks !== 1 ? 's' : ''}) →
                    </button>
                </footer>
            </form>
        </div>
    );
}
